import { SchoolApi } from 'api/schoolApi';
import { SubscribeApi } from 'api/subscribeApi';
import { Dropdown, SelectOption } from 'components/dropdown/Dropdown';
import { Spinner } from 'components/spinner/Spinner';
import { Form, Formik } from 'formik';
import { ICountry, SchoolParser } from 'pages/manageSchool/models';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import * as Yup from 'yup';

type MOStep1Props = {
  setCurrentStep: Dispatch<SetStateAction<number | string>>;
  membershipQuestions: any;
  setMembershipQuestions: Dispatch<any>;
  setMembershipQErr: Dispatch<SetStateAction<boolean>>;
};

type VATAddressDetails = {
  VATSchoolName: string;
  VATSchoolAddress: string;
};

export const MONonUK: React.FC<MOStep1Props> = ({
  setCurrentStep,
  membershipQuestions,
  setMembershipQuestions,
  setMembershipQErr,
}) => {
  const [initialDetails, setInitialDetails] =
    useState<any>(membershipQuestions);
  const [loading, setLoading] = useState<boolean>(false);
  const [vatLoading, setVatLoading] = useState<boolean>(false);
  const [savedVATCode, setSavedVATCode] = useState<string>('');
  const [vatAddressDetails, setVatAddressDetails] =
    useState<VATAddressDetails | null>(
      membershipQuestions.vatAddressDetails || null
    );

  const validationSchema = Yup.object({}).test({
    name: 'form validation eu country',
    test: function (
      this: Yup.TestContext,
      value: any
    ): boolean | Yup.ValidationError {
      const euCountrySelected =
        value && value.hasOwnProperty('selectedEUCountry')
          ? value.selectedEUCountry
          : null;

      if (
        (value.selectedLocationLabel && value.selectedLocationLabel.toLowerCase() === 'non-uk eu') &&
        !euCountrySelected
      ) {
        return this.createError({
          path: 'euCountries',
          message: 'Please select a country',
        });        
      } else {
        return true;
      }
    },
  }).test({
    name: 'form validation other country',
    test: function (
      this: Yup.TestContext,
      value: any
    ): boolean | Yup.ValidationError {
      const otherCountrySelected =
        value && value.hasOwnProperty('selectedOtherCountry')
          ? value.selectedOtherCountry
          : null;

      if (
        (value.selectedLocationLabel && value.selectedLocationLabel.toLowerCase() === 'other') &&
        !otherCountrySelected
      ) {
        return this.createError({
          path: 'otherCountries',
          message: 'Please select a country',
        });        
      } else {
        return true;
      }
    },
  });

  const handleSubmit = async (values: any, setFieldValue: any) => {
    if (!savedVATCode) {
      delete values['vatCode'];
      delete values['vatAddressDetails'];

      if (values.selectedLocationLabel.toLowerCase() === 'non-uk eu') {
        values['noVATNumberApplied'] = true;
        setMembershipQuestions(values);
        setCurrentStep('NonUKNoVAT');
        return;
      }
    } else {
      values['vatCode'] = savedVATCode;
      values['vatAddressDetails'] = vatAddressDetails;
      values['noVATNumberApplied'] = false;
    }

    setMembershipQuestions(values);
    setCurrentStep(2);
  };

  useEffect(() => {
    if (membershipQuestions && membershipQuestions.vatCode) {
      setSavedVATCode(membershipQuestions.vatCode);
    }
  }, []);

  const validateVATCode = async (values: any, setFieldError: any) => {
    setFieldError('vatCode', undefined);
    setVatLoading(true);
    setSavedVATCode("");

    if (values.selectedLocationLabel.toLowerCase() === 'non-uk eu' && !values.selectedEUCountry) {
      setFieldError('vatCode', 'Please select a country.');
      setVatLoading(false);
      return;
    }

    if (!values.vatCode) {
      setFieldError('vatCode', 'Please enter a VAT number');
      setVatLoading(false);
    }    

    try {
      const result = await SubscribeApi.validateVatNumber(
        values.vatCode,
        values.selectedEUCountry.value
      );

      if (
        !result.data &&
        result.error
      ) {
        setFieldError('vatCode', result.error.message);
        setVatLoading(false);
        return;
      } else if (
        result.data &&
        !result.data.validVatNumber
      ){
        setFieldError('vatCode', "Invalid Vat Number.");
      } else if (
        result.data &&
        result.data.validVatNumber
      ){
        setSavedVATCode(values.vatCode); 
        setFieldError('vatCode', undefined);

        setVatAddressDetails({
          VATSchoolName: result.data.companyName,
          VATSchoolAddress: result.data.companyAddress,
        });      
      }
    } catch (err) {
      setFieldError('vatCode', 'Sorry, something went wrong. Please try again.');   
    } finally {
      setVatLoading(false);
    }
  };

  const clearVATCode = (setFieldValue: any) => {
    setSavedVATCode('');
    setVatAddressDetails(null);
    setFieldValue('vatCode', '');
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <>
      <Formik
        initialValues={initialDetails}
        onSubmit={handleSubmit}
        validateOnChange={false}
        validateOnBlur={false}
        validateOnMount={false}
        enableReinitialize={true}
        validationSchema={validationSchema}
      >
        {(formik) => {
          let values: any = formik.values;
          let errors: any = formik.errors;
          let setErrors: any = formik.setErrors;

          return (
            <Form>
              <div className="MOQForm NonUK">
                {values.selectedLocationLabel.toLowerCase() === "non-uk eu" &&
                  <>
                    <div
                      className={
                        'FormBox CountryListBlock' +
                        (formik.errors.euCountries ? ' ErrorFB' : '')
                      }
                    >
                      <div className="FormLabel">
                        <label htmlFor="CountryListEU">Country</label>
                      </div>
                      <div className="FormField">
                        <Dropdown
                          id={'CountryListEU'}
                          options={values.euCountries}
                          value={values.selectedEUCountry}
                          onChange={(e: SelectOption) =>
                            formik.setFieldValue('selectedEUCountry', e)
                          }
                          customClassName="EditSingleSelect"
                          customNoOptionsMessage="No options"
                          placeholder={'Please select'}
                          roundedRectangle={true}
                        />
                        {formik.errors.euCountries && (
                          <p className="ErrorTxt">
                            {(formik as any).errors.euCountries}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="LineSpacer4"></div>

                    <div
                      className={
                        'FormBox VATCodeField' +
                        (vatLoading ? ' DisableVATField' : '') +
                        (formik.errors.vatCode ? ' ErrorFB' : '')
                      }
                    >
                      <div className="FormLabel">
                        <label htmlFor="VatCode">VAT number - optional</label>
                      </div>
                      <div className="FormField">
                        {vatLoading && <Spinner />}
                        <input
                          id="VatCode"
                          type="text"
                          className="EditBox"
                          onChange={(e) => {
                            formik.setFieldValue('vatCode', e.target.value);
                          }}
                          value={values.vatCode || ""}
                        />
                        <div
                          className={"Btn BtnOrange VATValidateBtn" + (savedVATCode ? " VATValidated" : "")}
                          onClick={() => validateVATCode(values, formik.setFieldError)}
                        >
                          {savedVATCode ? "Validated" : "Validate"}
                        </div>
                        {formik.errors.vatCode && (
                          <p className="ErrorTxt">
                            {(formik as any).errors.vatCode}
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                }

                {values.selectedLocationLabel.toLowerCase() === "other" &&
                  <div
                    className={
                      'FormBox CountryListBlock' +
                      (formik.errors.otherCountries ? ' ErrorFB' : '')
                    }
                  >
                    <div className="FormLabel">
                      <label htmlFor="CountryListOther">Country</label>
                    </div>
                    <div className="FormField">
                      <Dropdown
                        id={'CountryListOther'}
                        options={values.otherCountries}
                        value={values.selectedOtherCountry}
                        onChange={(e: SelectOption) =>
                          formik.setFieldValue('selectedOtherCountry', e)
                        }
                        customClassName="EditSingleSelect"
                        customNoOptionsMessage="No options"
                        placeholder={'Please select'}
                        roundedRectangle={true}
                      />
                      {formik.errors.otherCountries && (
                        <p className="ErrorTxt">
                          {(formik as any).errors.otherCountries}
                        </p>
                      )}
                    </div>
                  </div>
                }

                {vatAddressDetails && (
                  <div className="VATAddressDetails">
                    <div className="VATInfo">
                      <h4 className="VATSchoolName">
                        {vatAddressDetails.VATSchoolName}
                      </h4>
                      <p className="VATSchoolAddress">
                        {vatAddressDetails.VATSchoolAddress}
                      </p>
                    </div>
                    <div className="VATActionBtns">
                      <div
                        className="Btn BtnRed VATClearBtn"
                        onClick={() => clearVATCode(formik.setFieldValue)}
                      >
                        Clear
                      </div>
                    </div>
                  </div>
                )}

                <div
                  className="Btn BtnOrange SubmitBtn"
                  onClick={formik.submitForm}
                >
                  Next question
                </div>
                <div
                  className="Btn BtnWhite BackBtn"
                  onClick={() => setCurrentStep(1)}
                >
                  Back
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};
